import { Injectable } from '@angular/core';
import { combineLatest, Observable, of } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  map,
  shareReplay,
  startWith,
  switchMap,
} from 'rxjs/operators';
import { FrontendVerifyService } from '@apruv-client/services';
import { LayoutService } from '../../layout/service/layout-service.service';

interface VerifyAnalyticsAccessContext {
  companyId: number;
  isSuperUser: boolean;
  userRole?: string;
  userType?: string;
}

const LEGACY_VERIFY_ANALYTICS_COMPANY_IDS = [8];
const VERIFY_ANALYTICS_USER_TYPES = ['Admin', 'Manager', 'Sales'];
const VERIFY_ANALYTICS_USER_ROLES = ['Admin', 'Manager', 'Sales', 'Sales Rep'];

@Injectable({
  providedIn: 'root',
})
export class VerifyAnalyticsAccessService {
  readonly access$: Observable<boolean>;

  constructor(
    private layoutService: LayoutService,
    private verifyService: FrontendVerifyService
  ) {
    this.access$ = combineLatest([
      this.layoutService.companySelected$.pipe(
        startWith(this.layoutService.companySelected)
      ),
      this.layoutService.companyId$.pipe(
        startWith(this.layoutService.getCompanyId())
      ),
      this.layoutService.isSuperUser$.pipe(
        startWith(this.layoutService.superuser)
      ),
      this.layoutService.userRole$.pipe(startWith(this.layoutService.role)),
      this.layoutService.userType$.pipe(startWith(this.layoutService.userType)),
    ]).pipe(
      map(([company, companyId, isSuperUser, userRole, userType]) => ({
        companyId: this.resolveCompanyId(company?.id, companyId),
        isSuperUser: isSuperUser === true,
        userRole,
        userType,
      })),
      distinctUntilChanged(
        (previous, current) =>
          previous.companyId === current.companyId &&
          previous.isSuperUser === current.isSuperUser &&
          previous.userRole === current.userRole &&
          previous.userType === current.userType
      ),
      switchMap((context) => this.hasAccess(context)),
      distinctUntilChanged(),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  hasAccessForCurrentUser(): Observable<boolean> {
    return this.hasAccess({
      companyId: this.resolveCompanyId(this.layoutService.companySelected?.id),
      isSuperUser: this.layoutService.superuser === true,
      userRole: this.layoutService.role,
      userType: this.layoutService.userType,
    });
  }

  private hasAccess(
    context: VerifyAnalyticsAccessContext
  ): Observable<boolean> {
    if (context.isSuperUser) {
      return of(true);
    }

    if (!this.hasAllowedIdentity(context) || context.companyId <= 0) {
      return of(false);
    }

    return this.verifyService.getVerifyFeatureFlags(context.companyId).pipe(
      map(
        (flags) =>
          flags.verify_sales_analytics_access === true ||
          this.hasLegacyCompanyAccess(context.companyId)
      ),
      catchError(() => of(this.hasLegacyCompanyAccess(context.companyId)))
    );
  }

  private hasAllowedIdentity(context: VerifyAnalyticsAccessContext): boolean {
    return (
      (!!context.userType &&
        VERIFY_ANALYTICS_USER_TYPES.includes(context.userType)) ||
      (!!context.userRole &&
        VERIFY_ANALYTICS_USER_ROLES.includes(context.userRole))
    );
  }

  private hasLegacyCompanyAccess(companyId: number): boolean {
    return LEGACY_VERIFY_ANALYTICS_COMPANY_IDS.includes(companyId);
  }

  private resolveCompanyId(
    companyId?: number | null,
    fallbackCompanyId?: number | null
  ): number {
    const selectedCompanyId = Number(companyId);
    if (Number.isFinite(selectedCompanyId) && selectedCompanyId > 0) {
      return selectedCompanyId;
    }

    const fallbackId = Number(
      fallbackCompanyId ?? this.layoutService.getCompanyId()
    );
    if (Number.isFinite(fallbackId) && fallbackId > 0) {
      return fallbackId;
    }

    const sessionCompanyId = Number(sessionStorage.getItem('companyId') || 0);
    return Number.isFinite(sessionCompanyId) && sessionCompanyId > 0
      ? sessionCompanyId
      : 0;
  }
}
